import type { MedVisit } from "../../../../types/medchron";
import { compareCalendarDates, extractDate, parseCalendarDate } from "../../../../utils/dateUtils";
import type { VisitDisplay } from "../types";
import { ChronologySortField, SortDirection } from "../types";

export const collapseWhitespace = (value?: string | null): string =>
  (value ?? "").trim().replace(/\s+/g, " ");

export const normalizeGroupKey = (value?: string | null): string =>
  collapseWhitespace(value).toLowerCase();

const calendarDateKey = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const extracted = extractDate(value);
  return extracted && /^\d{4}-\d{2}-\d{2}$/.test(extracted) ? extracted : null;
};

export const sortVisitsByDate = (visitsArray: MedVisit[]): MedVisit[] =>
  [...visitsArray].sort((a, b) => compareCalendarDates(a.visit_date, b.visit_date));

// A visit without citations keys as file "" and page 0.
const firstCitationFileId = (v: VisitDisplay): string => v.citations[0]?.file_id ?? "";

const firstCitationStartPage = (v: VisitDisplay): number => v.citations[0]?.start_page ?? 0;

/** Milliseconds used for chronology ordering; null = missing date (sorts last). */
export const getVisitChronologyMillis = (v: VisitDisplay): number | null => {
  const dateKey =
    v.isCollapsed && v.startDate ? v.startDate : calendarDateKey(v.visit_date ?? null);
  if (!dateKey) return null;
  const parsed = parseCalendarDate(dateKey);
  return parsed?.getTime() ?? null;
};

const compareNullsLast = (a: number | null, b: number | null): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return 0;
};

const compareChronologyMillis = (
  a: number | null,
  b: number | null,
  direction: SortDirection
): number => {
  const nullCmp = compareNullsLast(a, b);
  if (nullCmp !== 0) return nullCmp;
  return direction === SortDirection.ASC ? a! - b! : b! - a!;
};

export const compareVisitsChronologically = (
  a: VisitDisplay,
  b: VisitDisplay,
  direction: SortDirection
): number => {
  const timeCmp = compareChronologyMillis(
    getVisitChronologyMillis(a),
    getVisitChronologyMillis(b),
    direction
  );
  if (timeCmp !== 0) return timeCmp;

  const fileCmp = firstCitationFileId(a).localeCompare(firstCitationFileId(b));
  if (fileCmp !== 0) return fileCmp;

  return firstCitationStartPage(a) - firstCitationStartPage(b);
};

/**
 * Orders visits by their first citation's file, then its start page — independent of visit_date.
 * - File order is always ascending; direction applies to the start page only.
 */
export const compareVisitsByPage = (
  a: VisitDisplay,
  b: VisitDisplay,
  direction: SortDirection
): number => {
  const fileCmp = firstCitationFileId(a).localeCompare(firstCitationFileId(b));
  if (fileCmp !== 0) return fileCmp;

  const pageCmp = firstCitationStartPage(a) - firstCitationStartPage(b);
  return direction === SortDirection.ASC ? pageCmp : -pageCmp;
};

export const sortVisitsForChronology = (
  visits: VisitDisplay[],
  field: ChronologySortField,
  direction: SortDirection,
  preserveOrder = false
): VisitDisplay[] => {
  if (preserveOrder) return visits;
  return field === ChronologySortField.PAGE
    ? [...visits].sort((a, b) => compareVisitsByPage(a, b, direction))
    : [...visits].sort((a, b) => compareVisitsChronologically(a, b, direction));
};

/** Earliest (or latest, for DESC) chronology millis across a group's visits; null if none dated. */
const groupChronologyAnchorMillis = (
  visits: VisitDisplay[],
  anchorDirection: SortDirection
): number | null => {
  let best: number | null = null;
  for (const v of visits) {
    const millis = getVisitChronologyMillis(v);
    if (millis === null) continue;
    if (best === null || (anchorDirection === SortDirection.DESC ? millis > best : millis < best)) {
      best = millis;
    }
  }
  return best;
};

// Empty labels always sort last, independent of `direction` — the direction only
// flips the alphabetical order among non-empty labels.
const compareGroupLabelsEmptyLast = (
  labelA: string,
  labelB: string,
  direction: SortDirection
): number => {
  if (!labelA && !labelB) return 0;
  if (!labelA) return 1;
  if (!labelB) return -1;
  const labelCmp = labelA.localeCompare(labelB, undefined, { sensitivity: "base" });
  return direction === SortDirection.DESC ? -labelCmp : labelCmp;
};

/**
 * Sorts visits within each group by field/direction.
 * - PAGE sort only reorders visits within a group; groups themselves stay in ascending date order.
 * - NAME sort orders groups alphabetically by their display label; visits inside each group stay oldest-first regardless of direction.
 * - When `preserveOrder` is true, returns `entries` unchanged — used while a search is active, to keep the caller's relevance ranking.
 */
export const sortChronologyGroupedEntries = (
  entries: [string, VisitDisplay[]][],
  field: ChronologySortField,
  direction: SortDirection,
  getGroupLabel: (key: string) => string = (key) => key,
  preserveOrder = false
): [string, VisitDisplay[]][] => {
  if (preserveOrder) return entries;
  if (field === ChronologySortField.NAME) {
    const withSortedVisits = entries.map(
      ([key, groupVisits]) =>
        [
          key,
          sortVisitsForChronology(groupVisits, ChronologySortField.DATE, SortDirection.ASC),
        ] as [string, VisitDisplay[]]
    );

    return [...withSortedVisits].sort(([keyA], [keyB]) =>
      compareGroupLabelsEmptyLast(getGroupLabel(keyA), getGroupLabel(keyB), direction)
    );
  }

  const withSortedVisits = entries.map(
    ([key, groupVisits]) =>
      [key, sortVisitsForChronology(groupVisits, field, direction)] as [string, VisitDisplay[]]
  );

  const anchorDirection = field === ChronologySortField.PAGE ? SortDirection.ASC : direction;

  return [...withSortedVisits].sort(([keyA, groupA], [keyB, groupB]) => {
    const timeCmp = compareChronologyMillis(
      groupChronologyAnchorMillis(groupA, anchorDirection),
      groupChronologyAnchorMillis(groupB, anchorDirection),
      anchorDirection
    );
    if (timeCmp !== 0) return timeCmp;

    return keyA.localeCompare(keyB, undefined, { sensitivity: "base" });
  });
};

export const groupTherapyVisits = (
  list: VisitDisplay[],
  collapseTherapyVisits: boolean,
  collapsibleVisitTypeIds: Set<string>,
  preserveOrder = false
): VisitDisplay[] => {
  if (!collapseTherapyVisits) return list;
  const regular = list.filter((v) => !collapsibleVisitTypeIds.has(v.visit_type_id));
  const collapsible = list.filter((v) => collapsibleVisitTypeIds.has(v.visit_type_id));
  const byKey = collapsible.reduce<
    Record<string, { visits: VisitDisplay[]; firstVisit: VisitDisplay }>
  >((acc, v) => {
    const key = `${v.visit_type_id}-${normalizeGroupKey(v.provider_name)}-${normalizeGroupKey(v.facility_name)}`;
    if (!acc[key]) acc[key] = { visits: [], firstVisit: v };
    acc[key].visits.push(v);
    return acc;
  }, {});
  const collapsed: VisitDisplay[] = Object.values(byKey).map(({ visits: vs, firstVisit }) => {
    const dates = vs
      .map((v) => calendarDateKey(v.visit_date ?? null))
      .filter((d): d is string => d !== null);
    const startDate = dates.length ? dates.reduce((min, d) => (d < min ? d : min)) : null;
    const endDate = dates.length ? dates.reduce((max, d) => (d > max ? d : max)) : null;
    return {
      ...firstVisit,
      isCollapsed: true,
      visitCount: vs.length,
      startDate,
      endDate,
      allVisits: vs,
    } as VisitDisplay;
  });
  if (preserveOrder) {
    const positionById = new Map(list.map((v, i) => [v.visit_id, i]));
    const rankOf = (v: VisitDisplay): number =>
      v.isCollapsed && v.allVisits
        ? Math.min(...v.allVisits.map((cv) => positionById.get(cv.visit_id) ?? Infinity))
        : (positionById.get(v.visit_id) ?? Infinity);
    return [...regular, ...collapsed].sort((a, b) => rankOf(a) - rankOf(b));
  }

  return [...regular, ...collapsed].sort((a, b) =>
    compareCalendarDates(a.startDate ?? a.visit_date, b.startDate ?? b.visit_date)
  );
};
